import { useEffect, useState, useRef, useCallback } from 'react'
import { useNavigate, Link, useSearch } from '@tanstack/react-router'
import {
  Building2,
  Calendar,
  Plus,
  X,
  Repeat,
  Filter,
  ChevronDown,
  Home,
  Pencil,
} from 'lucide-react'
import { useAuthStore } from '../lib/auth'
import {
  formatLocalDate,
  groupConsecutiveSlots,
  blockToTimeRange,
} from '../lib/utils'
import {
  api,
  type Room,
  type Booking,
  type RecurringBooking,
  type RecurringOccurrence,
  type Building,
  type OpeningSchedule,
  type RoomSchedule,
} from '../lib/api'
import { DashboardLayout } from '../components/DashboardLayout'
import { SetupWizard } from '../components/SetupWizard'
import { BookingDetailModal } from '../components/BookingDetailModal'
import { UnifiedBookingModal, type BookingSubmitData } from '../components/UnifiedBookingModal'
import { subscribeToSSE } from '../components/NotificationBell'
import FullCalendar from '@fullcalendar/react'
import dayGridPlugin from '@fullcalendar/daygrid'
import timeGridPlugin from '@fullcalendar/timegrid'
import interactionPlugin from '@fullcalendar/interaction'
import type { EventInput, DateSelectArg, EventClickArg } from '@fullcalendar/core'

// Helper for time formatting
const formatTime = (date: Date) => {
  return date.toTimeString().slice(0, 5) // HH:MM
}

const parseTime = (timeStr: string, date: Date) => {
  const parts = timeStr.split(':').map(Number)
  const hours = parts[0] ?? 0
  const minutes = parts[1] ?? 0
  const result = new Date(date)
  result.setHours(hours, minutes, 0, 0)
  return result
}

export function DashboardPage() {
  const navigate = useNavigate()
  const searchParams = useSearch({ strict: false }) as { action?: string }
  const { user, currentOrg, memberships, isAuthenticated, isLoading, checkAuth } = useAuthStore()
  const [rooms, setRooms] = useState<Room[]>([])
  const [roomsLoading, setRoomsLoading] = useState(true)
  const [buildings, setBuildings] = useState<Building[]>([])
  const [showSetupWizard, setShowSetupWizard] = useState(false)
  const [bookings, setBookings] = useState<Booking[]>([])
  const [recurringOccurrences, setRecurringOccurrences] = useState<RecurringOccurrence[]>([])
  const calendarRef = useRef<FullCalendar>(null)

  // Opening hours state for calendar overlay
  const [buildingSchedules, setBuildingSchedules] = useState<OpeningSchedule[]>([])
  const [roomSchedules, setRoomSchedules] = useState<RoomSchedule[]>([])

  // Filter state - for building/room filtering
  const [selectedBuildingId, setSelectedBuildingId] = useState<string | null>(null)
  const [selectedRoomId, setSelectedRoomId] = useState<string | null>(null)
  const [showFilterDropdown, setShowFilterDropdown] = useState(false)

  // Mobile detection for responsive calendar
  const [isMobile, setIsMobile] = useState(false)

  useEffect(() => {
    const checkMobile = () => setIsMobile(window.innerWidth < 768)
    checkMobile()
    window.addEventListener('resize', checkMobile)
    return () => window.removeEventListener('resize', checkMobile)
  }, [])

  // Booking modal state
  const [showBookingModal, setShowBookingModal] = useState(false)
  const [bookingModalRoom, setBookingModalRoom] = useState<Room | null>(null)
  const [bookingModalSchedules, setBookingModalSchedules] = useState<RoomSchedule[]>([])
  const [initialBookingValues, setInitialBookingValues] = useState<{
    date?: string
    startTime?: string
    endTime?: string
    slots?: string[]
    notes?: string
  }>({})

  // View booking modal
  const [selectedBooking, setSelectedBooking] = useState<Booking | null>(null)
  const [selectedRecurring, setSelectedRecurring] = useState<RecurringBooking | null>(null)
  const [selectedOccurrenceDate, setSelectedOccurrenceDate] = useState<string | null>(null)
  const [recurringBookings, setRecurringBookings] = useState<RecurringBooking[]>([])
  const [editingRecurringFields, setEditingRecurringFields] = useState(false)
  const [recurringEditForm, setRecurringEditForm] = useState({
    title: '',
    notes: '',
    startTime: '',
    endTime: '',
    roomId: '',
    endDate: '',
    isActive: true,
  })
  const [savingRecurring, setSavingRecurring] = useState(false)
  // Pending booking modal (shown after accepting invite from access request)
  const [pendingBookingData, setPendingBookingData] = useState<{
    roomId: string
    preferredDate: string
    preferredTime: string | null
    preferredSlots: string | null
    message: string | null
    orgId: string
  } | null>(null)
  const [showPendingBookingModal, setShowPendingBookingModal] = useState(false)

  useEffect(() => {
    checkAuth()
  }, [])

  useEffect(() => {
    if (!isLoading && !isAuthenticated) {
      navigate({ to: '/login' })
    }
  }, [isLoading, isAuthenticated, navigate])

  // The dashboard is the manage workspace. Anyone who doesn't manage an
  // organisation belongs on the personal My Bookings page.
  const managesAnyOrg = memberships.some((m) => m.role === 'admin' || m.role === 'approver')
  useEffect(() => {
    if (!isLoading && isAuthenticated && !managesAnyOrg) {
      navigate({ to: '/my-bookings' })
    }
  }, [isLoading, isAuthenticated, managesAnyOrg, navigate])

  useEffect(() => {
    if (isAuthenticated) {
      loadRooms()
    }
  }, [isAuthenticated])

  // Check for pending booking from access request after rooms are loaded
  useEffect(() => {
    if (rooms.length === 0 || roomsLoading) return

    const pendingBookingJson = localStorage.getItem('pendingBooking')
    if (pendingBookingJson) {
      try {
        const pending = JSON.parse(pendingBookingJson)
        if (pending.orgId === currentOrg?.orgId && rooms.find((r) => r.id === pending.roomId)) {
          setPendingBookingData(pending)
          setShowPendingBookingModal(true)
        }
        localStorage.removeItem('pendingBooking')
      } catch {
        localStorage.removeItem('pendingBooking')
      }
    }
  }, [rooms, roomsLoading, currentOrg?.orgId])

  // Handle action=newBooking search param
  useEffect(() => {
    if (
      searchParams?.action === 'newBooking' &&
      rooms.length > 0 &&
      rooms[0] &&
      !roomsLoading &&
      !showBookingModal
    ) {
      openBookingModal(rooms[0].id)
      // Clear the search param
      navigate({ to: '/dashboard', replace: true })
    }
  }, [searchParams?.action, rooms, roomsLoading, showBookingModal])

  // Subscribe to SSE booking updates for real-time refresh
  useEffect(() => {
    const unsubscribe = subscribeToSSE('booking_update', () => {
      // Reload bookings when we receive a booking update
      console.log('[Dashboard] Received booking_update, refreshing data...')
      loadRooms()
    })

    return () => unsubscribe()
  }, [])

  const loadRooms = async () => {
    try {
      setRoomsLoading(true)

      const buildingsData = await api.buildings.list()
      setBuildings(buildingsData.buildings)

      const currentMembership = memberships.find((m) => m.orgSlug === currentOrg?.orgSlug)
      if (buildingsData.buildings.length === 0 && currentMembership?.role === 'admin') {
        setShowSetupWizard(true)
      }

      const data = await api.rooms.list()
      setRooms(data.rooms)

      const today = new Date()
      today.setHours(0, 0, 0, 0)
      const pastDate = new Date(today)
      pastDate.setDate(today.getDate() - 7)
      const futureDate = new Date(today)
      futureDate.setDate(today.getDate() + 56)

      // Load bookings - this is required for all users
      try {
        const bookingsData = await api.bookings.list({
          start: pastDate.toISOString(),
          end: futureDate.toISOString(),
        })
        setBookings(bookingsData.bookings)
      } catch (bookingErr) {
        console.error('Failed to load bookings:', bookingErr)
        setBookings([])
      }

      // Load additional data for admins/approvers only (these APIs may not exist for guests)
      const isAdmin = currentMembership?.role === 'admin' || currentMembership?.role === 'approver'
      if (isAdmin) {
        try {
          const [recurringData, recurringListData] = await Promise.all([
            api.bookings.getRecurringOccurrences({
              start: pastDate.toISOString(),
              end: futureDate.toISOString(),
            }),
            api.bookings.listRecurring(),
          ])

          setRecurringOccurrences(recurringData.occurrences || [])
          setRecurringBookings(recurringListData.recurringBookings || [])
        } catch (adminErr) {
          console.error('Failed to load admin data:', adminErr)
          setRecurringOccurrences([])
          setRecurringBookings([])
        }
      } else {
        // For guests, set empty arrays
        setRecurringOccurrences([])
        setRecurringBookings([])
      }
    } catch (err) {
      console.error('Failed to load dashboard data:', err)
    } finally {
      setRoomsLoading(false)
    }
  }

  // Fetch opening schedules when filter changes
  useEffect(() => {
    const fetchSchedules = async () => {
      try {
        if (selectedRoomId) {
          const room = rooms.find((r) => r.id === selectedRoomId)
          if (room) {
            const scheduleData = await api.rooms.getSchedule(selectedRoomId)
            setRoomSchedules(scheduleData.schedules)
            if (room.buildingId) {
              const buildingScheduleData = await api.buildings.getOpeningSchedules(room.buildingId)
              setBuildingSchedules(buildingScheduleData.schedules)
            } else {
              setBuildingSchedules([])
            }
          }
        } else if (selectedBuildingId) {
          const buildingScheduleData = await api.buildings.getOpeningSchedules(selectedBuildingId)
          setBuildingSchedules(buildingScheduleData.schedules)
          setRoomSchedules([])
        } else {
          setBuildingSchedules([])
          setRoomSchedules([])
        }
      } catch (err) {
        // Ignore errors
      }
    }

    if (selectedRoomId && rooms.length > 0) {
      fetchSchedules()
    } else if (selectedBuildingId || !selectedRoomId) {
      fetchSchedules()
    }
  }, [selectedRoomId, selectedBuildingId, rooms])

  // Fetch room schedules for booking modal
  const loadRoomSchedules = async (roomId: string) => {
    try {
      const room = rooms.find((r) => r.id === roomId)
      if (!room) return

      setBookingModalRoom(room)

      const roomScheduleData = await api.rooms.getSchedule(roomId)
      if (roomScheduleData.schedules.length > 0) {
        setBookingModalSchedules(roomScheduleData.schedules)
      } else if (room.buildingId) {
        const buildingScheduleData = await api.buildings.getOpeningSchedules(room.buildingId)
        setBookingModalSchedules(
          buildingScheduleData.schedules.map((s) => ({
            id: `building-${s.id}`,
            roomId: roomId,
            dayOfWeek: s.dayOfWeek,
            openTime: s.openTime || '09:00',
            closeTime: s.closeTime || '17:00',
            isActive: s.isActive,
          }))
        )
      } else {
        setBookingModalSchedules(
          [0, 1, 2, 3, 4, 5, 6].map((day) => ({
            id: `default-${day}`,
            roomId: roomId,
            dayOfWeek: day,
            openTime: '09:00',
            closeTime: '17:00',
            isActive: day >= 1 && day <= 5,
          }))
        )
      }
    } catch {
      setBookingModalSchedules(
        [0, 1, 2, 3, 4, 5, 6].map((day) => ({
          id: `default-${day}`,
          roomId: roomId,
          dayOfWeek: day,
          openTime: '09:00',
          closeTime: '17:00',
          isActive: day >= 1 && day <= 5,
        }))
      )
    }
  }

  const fetchBookingAvailability = useCallback(
    async (roomId: string, startDate: string, endDate: string) => {
      try {
        const result = await api.bookings.list({ start: startDate, end: endDate })
        return result.bookings
          .filter((b) => b.roomId === roomId && b.status !== 'cancelled' && b.status !== 'denied')
          .map((b) => ({ startTime: b.startTime, endTime: b.endTime, status: b.status }))
      } catch (err) {
        console.error('Failed to load availability:', err)
        return []
      }
    },
    []
  )

  // Computed stats
  const todaysBookings = (() => {
    const todayStr = formatLocalDate(new Date())
    return bookings.filter((b) => {
      const bookingDate = formatLocalDate(new Date(b.startTime))
      return bookingDate === todayStr && b.status !== 'cancelled' && b.status !== 'denied'
    })
  })()

  const pendingOneTimeBookings = bookings.filter((b) => b.status === 'pending')
  const pendingApprovals = pendingOneTimeBookings

  // Helper to get day info for recurring patterns
  const getDayInfo = (date: Date) => {
    const dayOfWeek = date.getDay()
    const dayOfMonth = date.getDate()
    const weekOfMonth = Math.ceil(dayOfMonth / 7)
    return { dayOfWeek, dayOfMonth, weekOfMonth }
  }

  // Open booking modal
  const openBookingModal = async (
    roomId?: string,
    initialValues?: {
      date?: string
      startTime?: string
      endTime?: string
      slots?: string[]
      notes?: string
    }
  ) => {
    const targetRoomId = roomId || rooms[0]?.id
    if (!targetRoomId) return

    await loadRoomSchedules(targetRoomId)
    setInitialBookingValues(initialValues || {})
    setShowBookingModal(true)
  }

  // Calendar handlers
  const handleDateSelect = async (selectInfo: DateSelectArg) => {
    const startDate = selectInfo.start
    const endDate = selectInfo.end

    const dateStr = formatLocalDate(startDate)
    const startTimeStr = formatTime(startDate)

    let endTimeStr: string
    if (endDate.getDate() !== startDate.getDate()) {
      endTimeStr = formatTime(new Date(startDate.getTime() + 60 * 60 * 1000))
    } else {
      endTimeStr = formatTime(endDate)
    }

    await openBookingModal(rooms[0]?.id, {
      date: dateStr,
      startTime: startTimeStr,
      endTime: endTimeStr,
    })

    const calendarApi = selectInfo.view.calendar
    calendarApi.unselect()
  }

  const handleEventClick = (clickInfo: EventClickArg) => {
    const props = clickInfo.event.extendedProps
    if (props.type === 'booking' && props.bookingId) {
      const booking = bookings.find((b: Booking) => b.id === props.bookingId)
      if (booking && !booking.isRedacted) {
        setSelectedBooking(booking)
      }
    } else if (props.type === 'recurring' && props.recurringId) {
      const recurring = recurringBookings.find((r: RecurringBooking) => r.id === props.recurringId)
      if (recurring) {
        // Capture the occurrence date from the event start
        const eventDate = clickInfo.event.start
        setSelectedOccurrenceDate(eventDate ? formatLocalDate(eventDate) || null : null)
        setSelectedRecurring(recurring)
      }
    }
  }

  const handleDeleteRecurring = async (recurringId: string) => {
    if (!confirm('Delete this recurring booking? All future occurrences will be removed.')) {
      return
    }
    try {
      await api.bookings.deleteRecurring(recurringId)
      setSelectedRecurring(null)
      loadRooms()
    } catch (err: any) {
      alert(err.message || 'Failed to delete recurring booking')
    }
  }

  const formatRecurringPattern = (recurring: RecurringBooking) => {
    const dayNames = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday']
    const weekNames = ['1st', '2nd', '3rd', '4th', '5th']

    if (recurring.patternType === 'weekly' && recurring.dayOfWeek != null) {
      const interval = recurring.weekInterval || 1
      return interval === 2
        ? `Every other ${dayNames[recurring.dayOfWeek]}`
        : `Every ${dayNames[recurring.dayOfWeek]}`
    } else if (recurring.patternType === 'monthly_day' && recurring.dayOfMonth != null) {
      const d = recurring.dayOfMonth
      const suffix =
        d === 1 || d === 21 || d === 31
          ? 'st'
          : d === 2 || d === 22
            ? 'nd'
            : d === 3 || d === 23
              ? 'rd'
              : 'th'
      return `${d}${suffix} of every month`
    } else if (
      recurring.patternType === 'monthly_week' &&
      recurring.weekOfMonth != null &&
      recurring.dayOfWeek != null
    ) {
      return `${weekNames[recurring.weekOfMonth - 1]} ${dayNames[recurring.dayOfWeek]} of every month`
    }
    return recurring.patternType
  }

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
      </div>
    )
  }

  if (!isAuthenticated || !user) {
    return null
  }

  // Filter helpers
  const getFilteredRooms = (): Room[] => {
    if (selectedRoomId) return rooms.filter((r) => r.id === selectedRoomId)
    if (selectedBuildingId) return rooms.filter((r) => r.buildingId === selectedBuildingId)
    return rooms
  }

  const getFilterDisplayText = (): string => {
    if (selectedRoomId) {
      const room = rooms.find((r) => r.id === selectedRoomId)
      return room?.name || 'Selected Room'
    }
    if (selectedBuildingId) {
      const building = buildings.find((b) => b.id === selectedBuildingId)
      return building?.name || 'Selected Building'
    }
    return 'All Rooms'
  }

  const clearFilter = () => {
    setSelectedBuildingId(null)
    setSelectedRoomId(null)
  }

  // Generate calendar events
  const generateCalendarEvents = () => {
    const events: EventInput[] = []
    const filteredRooms = getFilteredRooms()
    const filteredRoomIds = new Set(filteredRooms.map((r) => r.id))

    const roomIndexMap = new Map<string, number>(
      rooms.map((room: Room, index: number) => [room.id, index])
    )
    const colors = [
      { bg: 'var(--rs-teal-50)', border: 'var(--rs-teal-500)', text: 'var(--rs-teal-700)' },
      { bg: 'var(--rs-coral-50)', border: 'var(--rs-coral-500)', text: 'var(--rs-coral-700)' },
      { bg: 'var(--rs-blue-50)', border: 'var(--rs-blue-500)', text: 'var(--rs-blue-700)' },
      { bg: 'var(--rs-amber-50)', border: 'var(--rs-amber-500)', text: 'var(--rs-amber-700)' },
      { bg: 'var(--rs-gray-100)', border: 'var(--rs-gray-600)', text: 'var(--rs-gray-900)' },
      { bg: 'var(--rs-green-50)', border: 'var(--rs-green-500)', text: 'var(--rs-green-700)' },
    ]
    const defaultColor = colors[0]

    // Add booking events
    bookings.forEach((booking: Booking) => {
      if (!filteredRoomIds.has(booking.roomId)) return

      const roomIndex = roomIndexMap.get(booking.roomId) ?? 0
      const color = colors[roomIndex % colors.length] || defaultColor
      const room = rooms.find((r: Room) => r.id === booking.roomId)

      if (!color) return

      events.push({
        id: `booking-${booking.id}`,
        title: booking.title || room?.name || 'Booking',
        start: booking.startTime,
        end: booking.endTime,
        backgroundColor: color.bg,
        borderColor: color.border,
        textColor: color.text,
        extendedProps: {
          type: 'booking',
          bookingId: booking.id,
          roomId: booking.roomId,
          roomName: room?.name || 'Unknown Room',
          status: booking.status,
        },
      })
    })

    // Add recurring booking occurrences
    recurringOccurrences.forEach((occ: RecurringOccurrence) => {
      if (!filteredRoomIds.has(occ.roomId)) return

      const roomIndex = roomIndexMap.get(occ.roomId) ?? 0
      const color = colors[roomIndex % colors.length] || defaultColor
      const room = rooms.find((r: Room) => r.id === occ.roomId)

      if (!color) return

      events.push({
        id: `recurring-${occ.id}`,
        title: occ.title || room?.name || 'Recurring Booking',
        start: occ.startTime,
        end: occ.endTime,
        backgroundColor: color.bg,
        borderColor: color.border,
        textColor: color.text,
        extendedProps: {
          type: 'recurring',
          recurringId: occ.recurringBookingId,
          roomId: occ.roomId,
          roomName: room?.name || 'Unknown Room',
          patternType: occ.patternType,
          status: occ.status,
        },
      })
    })

    // Add closed-time background events
    const activeSchedules = roomSchedules.length > 0 ? roomSchedules : buildingSchedules

    if ((selectedBuildingId || selectedRoomId) && activeSchedules.length > 0) {
      const startOfWeekSchedule = new Date()
      startOfWeekSchedule.setDate(startOfWeekSchedule.getDate() - startOfWeekSchedule.getDay())
      startOfWeekSchedule.setHours(0, 0, 0, 0)

      for (let weekOffset = 0; weekOffset < 8; weekOffset++) {
        for (let dayOfWeek = 0; dayOfWeek < 7; dayOfWeek++) {
          const currentDate = new Date(startOfWeekSchedule)
          currentDate.setDate(startOfWeekSchedule.getDate() + weekOffset * 7 + dayOfWeek)
          const dateStr = formatLocalDate(currentDate)

          const schedule = activeSchedules.find((s) => s.dayOfWeek === dayOfWeek)

          if (!schedule || !schedule.isActive) {
            events.push({
              id: `closed-${dateStr}`,
              title: 'Closed',
              start: `${dateStr}T06:00:00`,
              end: `${dateStr}T22:00:00`,
              display: 'background',
              backgroundColor: 'rgba(156, 163, 175, 0.3)',
              extendedProps: { type: 'closed' },
            })
          } else {
            const openTime = schedule.openTime || '09:00'
            const closeTime = schedule.closeTime || '17:00'

            if (openTime > '06:00') {
              events.push({
                id: `closed-morning-${dateStr}`,
                title: '',
                start: `${dateStr}T06:00:00`,
                end: `${dateStr}T${openTime}:00`,
                display: 'background',
                backgroundColor: 'rgba(156, 163, 175, 0.3)',
                extendedProps: { type: 'closed' },
              })
            }

            if (closeTime < '22:00') {
              events.push({
                id: `closed-evening-${dateStr}`,
                title: '',
                start: `${dateStr}T${closeTime}:00`,
                end: `${dateStr}T22:00:00`,
                display: 'background',
                backgroundColor: 'rgba(156, 163, 175, 0.3)',
                extendedProps: { type: 'closed' },
              })
            }
          }
        }
      }
    }

    return events
  }

  return (
    <>
      {/* Setup Wizard for new tenants - rendered outside DashboardLayout */}
      {showSetupWizard && (
        <SetupWizard
          onComplete={() => {
            setShowSetupWizard(false)
            loadRooms()
          }}
        />
      )}

      <DashboardLayout title="Calendar">
        {/* Guest account type AND not invited to this org as guest role */}
        {currentOrg?.accountType === 'guest' && currentOrg?.role !== 'guest' && (
          <div className="max-w-2xl mx-auto">
            <div className="bg-white rounded-xl border border-gray-200 p-8 text-center">
              <div className="w-16 h-16 bg-primary-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <Building2 className="w-8 h-8 text-primary-600" />
              </div>
              <h2 className="text-2xl font-bold text-gray-900 mb-2">Welcome to Roomsi!</h2>
              <p className="text-gray-600 mb-6">
                Ready to share your space? Add your first room to start accepting bookings.
              </p>
              <Link
                to="/dashboard/rooms/new"
                className="inline-flex items-center gap-2 px-6 py-3 bg-primary-600 text-white font-medium rounded-lg hover:bg-primary-700 transition-colors"
              >
                <Plus className="w-5 h-5" />
                Add Your First Room
              </Link>
            </div>
          </div>
        )}

        {/* Manage workspace (guests are redirected to /my-bookings) */}
        {currentOrg?.accountType !== 'guest' && (
          <>
            {
              <>
                {/* Admin/Approver Stats */}
                <div className="grid grid-cols-3 gap-2 sm:gap-6 mb-4 sm:mb-8">
                  <StatCard
                    title="Today's Bookings"
                    shortTitle="Today"
                    value={todaysBookings.length.toString()}
                    description={
                      todaysBookings.length === 0
                        ? 'No bookings yet'
                        : `${todaysBookings.length} booking${todaysBookings.length === 1 ? '' : 's'} today`
                    }
                    shortDescription={
                      todaysBookings.length === 0 ? 'No bookings' : `${todaysBookings.length} today`
                    }
                  />
                  <StatCard
                    title="Available Rooms"
                    shortTitle="Rooms"
                    value={roomsLoading ? '...' : rooms.length.toString()}
                    description={
                      rooms.length === 0
                        ? 'Add rooms to get started'
                        : `${rooms.filter((r) => r.isPublic).length} public`
                    }
                    shortDescription={`${rooms.filter((r) => r.isPublic).length} public`}
                  />
                  <StatCard
                    title="Pending Approvals"
                    shortTitle="Pending"
                    value={pendingApprovals.length.toString()}
                    description={
                      pendingApprovals.length === 0
                        ? 'All caught up!'
                        : `${pendingApprovals.length} awaiting review`
                    }
                    shortDescription={
                      pendingApprovals.length === 0
                        ? 'All done'
                        : `${pendingApprovals.length} to review`
                    }
                    onClick={
                      pendingApprovals.length > 0
                        ? () =>
                            navigate({ to: '/dashboard/bookings', search: { filter: 'pending' } })
                        : undefined
                    }
                  />
                </div>

                {/* Admin quick action: Direct Booking */}
                {rooms.length > 0 && (
                  <div className="mb-4 sm:mb-6">
                    <button
                      onClick={() => {
                        if (rooms[0]) {
                          openBookingModal(rooms[0].id)
                        }
                      }}
                      className="inline-flex items-center gap-2 px-4 py-2 bg-primary-600 text-white text-sm font-medium rounded-lg hover:bg-primary-700 transition-colors"
                    >
                      <Plus className="w-4 h-4" />
                      Direct Booking
                    </button>
                  </div>
                )}

                {/* Quick actions when no rooms */}
                {rooms.length === 0 && (
                  <div className="bg-white rounded-xl border border-gray-200 p-4 sm:p-6 mb-4 sm:mb-8">
                    <h2 className="text-base sm:text-lg font-semibold text-gray-900 mb-3 sm:mb-4">
                      Get Started
                    </h2>
                    <div className="grid md:grid-cols-2 gap-3 sm:gap-4">
                      <QuickAction
                        icon={Building2}
                        title="Add your first room"
                        description="Create a room with descriptions, images, and availability"
                        href="/dashboard/rooms/new"
                      />
                      <QuickAction
                        icon={Calendar}
                        title="Invite team members"
                        description="Bring your team on board and set their permissions"
                        href="/dashboard/people"
                      />
                    </div>
                  </div>
                )}

                {/* Calendar */}
                {rooms.length > 0 && (
                  <div className="bg-white rounded-xl border border-gray-200 p-3 sm:p-6">
                    <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-4">
                      <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-4">
                        <h2 className="text-base sm:text-lg font-semibold text-gray-900">
                          Bookings & Schedule
                        </h2>

                        {/* Filter Dropdown */}
                        <div className="relative">
                          <button
                            onClick={() => setShowFilterDropdown(!showFilterDropdown)}
                            className={`inline-flex items-center gap-2 px-3 py-1.5 text-sm rounded-lg border transition-colors ${
                              selectedBuildingId || selectedRoomId
                                ? 'bg-primary-50 border-primary-200 text-primary-700'
                                : 'bg-gray-50 border-gray-200 text-gray-600 hover:bg-gray-100'
                            }`}
                          >
                            <Filter className="w-4 h-4" />
                            <span>{getFilterDisplayText()}</span>
                            <ChevronDown className="w-4 h-4" />
                          </button>

                          {showFilterDropdown && (
                            <div className="absolute top-full left-0 mt-1 w-64 bg-white rounded-lg shadow-lg border border-gray-200 z-50 py-2">
                              <button
                                onClick={() => {
                                  clearFilter()
                                  setShowFilterDropdown(false)
                                }}
                                className={`w-full text-left px-4 py-2 text-sm hover:bg-gray-50 ${
                                  !selectedBuildingId && !selectedRoomId
                                    ? 'bg-primary-50 text-primary-700 font-medium'
                                    : 'text-gray-700'
                                }`}
                              >
                                All Rooms
                              </button>

                              {buildings.map((building) => {
                                const buildingRooms = rooms.filter(
                                  (r: Room) => r.buildingId === building.id
                                )
                                if (buildingRooms.length === 0) return null

                                return (
                                  <div
                                    key={building.id}
                                    className="border-t border-gray-100 first:border-t-0"
                                  >
                                    <button
                                      onClick={() => {
                                        setSelectedBuildingId(building.id)
                                        setSelectedRoomId(null)
                                        setShowFilterDropdown(false)
                                      }}
                                      className={`w-full text-left px-4 py-2 text-sm font-medium hover:bg-gray-50 flex items-center gap-2 ${
                                        selectedBuildingId === building.id && !selectedRoomId
                                          ? 'bg-primary-50 text-primary-700'
                                          : 'text-gray-900'
                                      }`}
                                    >
                                      <Building2 className="w-4 h-4" />
                                      {building.name}
                                    </button>

                                    {buildingRooms.map((room: Room) => (
                                      <button
                                        key={room.id}
                                        onClick={() => {
                                          setSelectedBuildingId(building.id)
                                          setSelectedRoomId(room.id)
                                          setShowFilterDropdown(false)
                                        }}
                                        className={`w-full text-left pl-10 pr-4 py-1.5 text-sm hover:bg-gray-50 ${
                                          selectedRoomId === room.id
                                            ? 'bg-primary-50 text-primary-700 font-medium'
                                            : 'text-gray-600'
                                        }`}
                                      >
                                        {room.name}
                                      </button>
                                    ))}
                                  </div>
                                )
                              })}

                              {rooms.filter((r: Room) => !r.buildingId).length > 0 && (
                                <div className="border-t border-gray-100">
                                  <div className="px-4 py-1.5 text-xs font-medium text-gray-400 uppercase">
                                    Unassigned Rooms
                                  </div>
                                  {rooms
                                    .filter((r: Room) => !r.buildingId)
                                    .map((room: Room) => (
                                      <button
                                        key={room.id}
                                        onClick={() => {
                                          setSelectedBuildingId(null)
                                          setSelectedRoomId(room.id)
                                          setShowFilterDropdown(false)
                                        }}
                                        className={`w-full text-left px-4 py-1.5 text-sm hover:bg-gray-50 ${
                                          selectedRoomId === room.id
                                            ? 'bg-primary-50 text-primary-700 font-medium'
                                            : 'text-gray-600'
                                        }`}
                                      >
                                        {room.name}
                                      </button>
                                    ))}
                                </div>
                              )}
                            </div>
                          )}
                        </div>

                        {(selectedBuildingId || selectedRoomId) && (
                          <button
                            onClick={clearFilter}
                            className="text-xs text-gray-500 hover:text-gray-700"
                          >
                            Clear filter
                          </button>
                        )}
                      </div>
                    </div>

                    <div className="calendar-container">
                      <FullCalendar
                        ref={calendarRef}
                        plugins={[dayGridPlugin, timeGridPlugin, interactionPlugin]}
                        initialView={isMobile ? 'timeGridDay' : 'timeGridWeek'}
                        headerToolbar={
                          isMobile
                            ? {
                                left: 'prev,next',
                                center: 'title',
                                right: 'timeGridDay,timeGridWeek',
                              }
                            : {
                                left: 'prev,next today',
                                center: 'title',
                                right: 'dayGridMonth,timeGridWeek,timeGridDay',
                              }
                        }
                        events={generateCalendarEvents()}
                        slotMinTime="06:00:00"
                        slotMaxTime="22:00:00"
                        allDaySlot={false}
                        weekends={true}
                        nowIndicator={true}
                        height="auto"
                        contentHeight={isMobile ? 400 : 'auto'}
                        eventDisplay="block"
                        selectable={true}
                        selectMirror={true}
                        select={handleDateSelect}
                        eventClick={handleEventClick}
                        eventContent={(eventInfo) => {
                          const type = eventInfo.event.extendedProps.type
                          const roomName = eventInfo.event.extendedProps.roomName
                          const isRecurringEvent = type === 'recurring'
                          const isCleaning = type === 'cleaning'
                          const cleanTitle = eventInfo.event.title.replace(/^🔄\s*/, '')

                          return (
                            <div className="fc-event-main-frame overflow-hidden h-full px-1.5 py-0.5 relative">
                              <div className="text-xs font-medium truncate leading-tight">
                                {cleanTitle}
                              </div>
                              {roomName && !isCleaning && (
                                <div className="text-[10px] opacity-80 truncate leading-tight">
                                  {roomName}
                                </div>
                              )}
                              {isRecurringEvent && (
                                <div
                                  className="absolute bottom-0 right-0 text-[10px] opacity-70"
                                  title="Recurring booking"
                                >
                                  🔄
                                </div>
                              )}
                            </div>
                          )
                        }}
                        eventDidMount={(info) => {
                          const status = info.event.extendedProps.status
                          const type = info.event.extendedProps.type
                          const borderColor = info.event.borderColor || '#6b7280'
                          const isPending = status === 'pending'
                          const isCleaning = type === 'cleaning'

                          const el = info.el

                          if ((type === 'booking' || type === 'recurring') && !isCleaning) {
                            if (isPending) {
                              el.style.backgroundImage = `repeating-linear-gradient(-45deg, transparent, transparent 3px, rgba(255,255,255,0.4) 3px, rgba(255,255,255,0.4) 6px)`
                              el.style.borderColor = borderColor
                              el.style.borderWidth = '1px'
                              el.style.borderStyle = 'dashed'
                            } else {
                              el.style.borderColor = borderColor
                              el.style.borderWidth = '2px'
                              el.style.borderLeftWidth = '4px'
                              el.style.borderStyle = 'solid'
                            }
                          }
                        }}
                        slotLabelFormat={{ hour: 'numeric', minute: '2-digit', meridiem: 'short' }}
                        dayHeaderFormat={
                          isMobile
                            ? { weekday: 'short' }
                            : {
                                weekday: 'short',
                                month: 'numeric',
                                day: 'numeric',
                                omitCommas: true,
                              }
                        }
                        titleFormat={
                          isMobile
                            ? { month: 'short', day: 'numeric' }
                            : { year: 'numeric', month: 'short', day: 'numeric' }
                        }
                      />
                    </div>
                  </div>
                )}
              </>
            }
          </>
        )}

        {/* Create Booking Modal */}
        {showBookingModal && bookingModalRoom && (
          <UnifiedBookingModal
            room={bookingModalRoom}
            roomName={bookingModalRoom.name}
            schedules={bookingModalSchedules}
            currency="GBP"
            canBookOutOfHours={
              memberships.find((m) => m.orgId === currentOrg?.orgId)?.canBookOutOfHours || false
            }
            allowOutOfHours={currentOrg?.role === 'admin' || currentOrg?.role === 'approver'}
            mode="member"
            rooms={rooms}
            initialDate={initialBookingValues.date}
            initialStartTime={initialBookingValues.startTime}
            initialEndTime={initialBookingValues.endTime}
            initialSlots={initialBookingValues.slots}
            initialNotes={initialBookingValues.notes}
            onRoomChange={async (newRoomId) => {
              const newRoom = rooms.find((r) => r.id === newRoomId)
              if (newRoom) {
                await loadRoomSchedules(newRoomId)
              }
            }}
            onClose={() => {
              setShowBookingModal(false)
              setInitialBookingValues({})
              loadRooms()
            }}
            onSubmit={async (data: BookingSubmitData) => {
              const dateObj = new Date(data.date + 'T00:00:00')

              if (data.isRecurring && data.recurringPattern) {
                const { dayOfWeek, dayOfMonth, weekOfMonth } = getDayInfo(dateObj)

                await api.bookings.createRecurring({
                  roomId: data.roomId,
                  title: data.title || undefined,
                  patternType: data.recurringPattern,
                  dayValue:
                    data.recurringPattern === 'weekly'
                      ? dayOfWeek
                      : data.recurringPattern === 'monthly_day'
                        ? dayOfMonth
                        : undefined,
                  weekOfMonth: data.recurringPattern === 'monthly_week' ? weekOfMonth : undefined,
                  dayOfWeek: data.recurringPattern === 'monthly_week' ? dayOfWeek : undefined,
                  startTime: data.startTime,
                  endTime: data.endTime,
                  startDate: data.date,
                  endDate: data.recurringEndDate || undefined,
                  notes: data.notes || undefined,
                  weekInterval: data.weekInterval || undefined,
                })
              } else {
                // Group selected slots into consecutive blocks
                // Each block becomes a separate booking
                const blocks =
                  data.selectedSlots && data.selectedSlots.length > 0
                    ? groupConsecutiveSlots(data.selectedSlots)
                    : [[data.startTime]] // Fallback to single booking using start/end times

                let firstBooking: { id: string; status: string } | null = null

                // If only one block, use the original start/end times (preserves endTime correctly)
                if (blocks.length === 1 && !data.selectedSlots?.length) {
                  const startTime = parseTime(data.startTime, dateObj)
                  const endTime = parseTime(data.endTime, dateObj)
                  const result = await api.bookings.create({
                    roomId: data.roomId,
                    startTime: startTime.toISOString(),
                    endTime: endTime.toISOString(),
                    localStartTime: data.startTime,
                    localEndTime: data.endTime,
                    title: data.title,
                    notes: data.notes,
                    donationAmount: data.donationAmount,
                  })
                  firstBooking = result.booking
                } else {
                  // Create separate bookings for each consecutive block
                  for (const block of blocks) {
                    const { start, end } = blockToTimeRange(block)
                    const startTime = parseTime(start, dateObj)
                    const endTime = parseTime(end, dateObj)

                    // For multiple bookings, append block info to title if no title provided
                    const blockTitle =
                      blocks.length > 1 && !data.title ? `Booking (${start})` : data.title

                    const result = await api.bookings.create({
                      roomId: data.roomId,
                      startTime: startTime.toISOString(),
                      endTime: endTime.toISOString(),
                      localStartTime: start,
                      localEndTime: end,
                      title: blockTitle,
                      notes: data.notes,
                      // A pledged donation belongs to the first booking only
                      donationAmount: firstBooking ? undefined : data.donationAmount,
                    })
                    if (!firstBooking) firstBooking = result.booking
                  }
                }

                // With a pledged donation the modal stays open so it can hand the guest
                // over to the payment page (or explain what happens next).
                if (!data.donationAmount) {
                  setShowBookingModal(false)
                  setInitialBookingValues({})
                }
                await loadRooms()
                return firstBooking
                  ? { booking: { id: firstBooking.id, status: firstBooking.status } }
                  : undefined
              }

              setShowBookingModal(false)
              setInitialBookingValues({})
              await loadRooms()
            }}
            fetchAvailability={fetchBookingAvailability}
            showRecurring={true}
            submitButtonText="Create Booking"
            title="Direct Booking"
          />
        )}

        {/* View Booking Modal */}
        {selectedBooking && (
          <BookingDetailModal
            booking={selectedBooking}
            room={rooms.find((r) => r.id === selectedBooking.roomId)}
            onClose={() => setSelectedBooking(null)}
            onUpdate={() => loadRooms()}
          />
        )}

        {/* View/Edit Recurring Booking Modal */}
        {selectedRecurring && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
            <div className="bg-white rounded-xl shadow-xl w-full max-w-md mx-4 p-6 max-h-[90vh] overflow-y-auto">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
                  <Repeat className="w-5 h-5 text-primary-600" />
                  Recurring Booking
                </h2>
                <div className="flex items-center gap-1">
                  {!editingRecurringFields && (
                    <button
                      onClick={() => {
                        setEditingRecurringFields(true)
                        setRecurringEditForm({
                          title: selectedRecurring.title || '',
                          notes: selectedRecurring.notes || '',
                          startTime: selectedRecurring.startTime,
                          endTime: selectedRecurring.endTime,
                          roomId: selectedRecurring.roomId,
                          endDate: selectedRecurring.endDate || '',
                          isActive: selectedRecurring.isActive,
                        })
                      }}
                      className="p-1.5 text-gray-400 hover:text-primary-600 transition-colors"
                      title="Edit series"
                    >
                      <Pencil className="w-4 h-4" />
                    </button>
                  )}
                  <button
                    onClick={() => {
                      setSelectedRecurring(null)
                      setEditingRecurringFields(false)
                    }}
                    className="p-1 text-gray-400 hover:text-gray-600"
                  >
                    <X className="w-5 h-5" />
                  </button>
                </div>
              </div>

              {editingRecurringFields ? (
                /* Edit mode */
                <div className="space-y-3">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Title</label>
                    <input
                      type="text"
                      value={recurringEditForm.title}
                      onChange={(e) =>
                        setRecurringEditForm({ ...recurringEditForm, title: e.target.value })
                      }
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                      placeholder="Booking title"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Room</label>
                    <select
                      value={recurringEditForm.roomId}
                      onChange={(e) =>
                        setRecurringEditForm({ ...recurringEditForm, roomId: e.target.value })
                      }
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                    >
                      {rooms.map((room) => (
                        <option key={room.id} value={room.id}>
                          {room.name}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Start Time
                      </label>
                      <input
                        type="time"
                        value={recurringEditForm.startTime}
                        onChange={(e) =>
                          setRecurringEditForm({ ...recurringEditForm, startTime: e.target.value })
                        }
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        End Time
                      </label>
                      <input
                        type="time"
                        value={recurringEditForm.endTime}
                        onChange={(e) =>
                          setRecurringEditForm({ ...recurringEditForm, endTime: e.target.value })
                        }
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                      />
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">End Date</label>
                    <input
                      type="date"
                      value={recurringEditForm.endDate}
                      onChange={(e) =>
                        setRecurringEditForm({ ...recurringEditForm, endDate: e.target.value })
                      }
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                    />
                    {recurringEditForm.endDate && (
                      <button
                        onClick={() => setRecurringEditForm({ ...recurringEditForm, endDate: '' })}
                        className="text-xs text-gray-500 hover:text-gray-700 mt-1"
                      >
                        Clear end date (make indefinite)
                      </button>
                    )}
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Notes</label>
                    <textarea
                      value={recurringEditForm.notes}
                      onChange={(e) =>
                        setRecurringEditForm({ ...recurringEditForm, notes: e.target.value })
                      }
                      rows={2}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                      placeholder="Optional notes"
                    />
                  </div>
                  <div className="flex items-center gap-2">
                    <label className="text-sm font-medium text-gray-700">Active</label>
                    <button
                      onClick={() =>
                        setRecurringEditForm({
                          ...recurringEditForm,
                          isActive: !recurringEditForm.isActive,
                        })
                      }
                      className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                        recurringEditForm.isActive ? 'bg-primary-600' : 'bg-gray-300'
                      }`}
                    >
                      <span
                        className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                          recurringEditForm.isActive ? 'translate-x-6' : 'translate-x-1'
                        }`}
                      />
                    </button>
                  </div>
                  <div className="bg-gray-50 rounded-lg px-3 py-2">
                    <div className="text-sm text-gray-500">Pattern (cannot be changed)</div>
                    <div className="font-medium text-sm">
                      {formatRecurringPattern(selectedRecurring)}
                    </div>
                  </div>
                  <div className="flex justify-end gap-3 mt-4">
                    <button
                      onClick={() => setEditingRecurringFields(false)}
                      className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
                    >
                      Cancel
                    </button>
                    <button
                      disabled={savingRecurring}
                      onClick={async () => {
                        setSavingRecurring(true)
                        try {
                          await api.bookings.updateRecurring(selectedRecurring.id, {
                            title: recurringEditForm.title || undefined,
                            notes: recurringEditForm.notes || undefined,
                            startTime: recurringEditForm.startTime,
                            endTime: recurringEditForm.endTime,
                            roomId: recurringEditForm.roomId,
                            endDate: recurringEditForm.endDate || null,
                            isActive: recurringEditForm.isActive,
                          })
                          setEditingRecurringFields(false)
                          setSelectedRecurring(null)
                          await loadRooms()
                        } catch (err: any) {
                          alert(err.message || 'Failed to save changes')
                        } finally {
                          setSavingRecurring(false)
                        }
                      }}
                      className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors disabled:opacity-50"
                    >
                      {savingRecurring ? 'Saving...' : 'Save Changes'}
                    </button>
                  </div>
                </div>
              ) : (
                /* View mode */
                <>
                  <div className="space-y-3">
                    <div>
                      <div className="text-sm text-gray-500">Room</div>
                      <div className="font-medium">
                        {selectedRecurring.roomName ||
                          rooms.find((r) => r.id === selectedRecurring.roomId)?.name ||
                          'Unknown'}
                      </div>
                    </div>
                    <div>
                      <div className="text-sm text-gray-500">Title</div>
                      <div className="font-medium">{selectedRecurring.title || 'No title'}</div>
                    </div>
                    <div>
                      <div className="text-sm text-gray-500">Pattern</div>
                      <div className="font-medium">{formatRecurringPattern(selectedRecurring)}</div>
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <div className="text-sm text-gray-500">Time</div>
                        <div className="font-medium">
                          {selectedRecurring.startTime} - {selectedRecurring.endTime}
                        </div>
                      </div>
                      <div>
                        <div className="text-sm text-gray-500">Status</div>
                        <div
                          className={`inline-block px-2 py-1 rounded text-sm font-medium ${
                            selectedRecurring.isActive
                              ? 'bg-green-100 text-green-700'
                              : 'bg-gray-100 text-gray-700'
                          }`}
                        >
                          {selectedRecurring.isActive ? 'Active' : 'Paused'}
                        </div>
                      </div>
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <div className="text-sm text-gray-500">Start Date</div>
                        <div className="font-medium">
                          {new Date(selectedRecurring.startDate + 'T00:00:00').toLocaleDateString()}
                        </div>
                      </div>
                      <div>
                        <div className="text-sm text-gray-500">End Date</div>
                        <div className="font-medium">
                          {selectedRecurring.endDate
                            ? new Date(selectedRecurring.endDate + 'T00:00:00').toLocaleDateString()
                            : 'No end date'}
                        </div>
                      </div>
                    </div>
                    {selectedRecurring.createdBy && (
                      <div>
                        <div className="text-sm text-gray-500">Created by</div>
                        <div className="font-medium">{selectedRecurring.createdBy.name}</div>
                      </div>
                    )}
                    {selectedRecurring.notes && (
                      <div>
                        <div className="text-sm text-gray-500">Notes</div>
                        <div className="text-gray-700">{selectedRecurring.notes}</div>
                      </div>
                    )}
                    {selectedRecurring.exceptions && selectedRecurring.exceptions.length > 0 && (
                      <div>
                        <div className="text-sm text-gray-500 mb-1">Cancelled dates</div>
                        <div className="space-y-1">
                          {selectedRecurring.exceptions.map((ex) => (
                            <div
                              key={ex.id}
                              className="flex items-center justify-between text-sm bg-gray-50 rounded px-2 py-1"
                            >
                              <span className="text-gray-700">
                                {new Date(ex.exceptionDate + 'T00:00:00').toLocaleDateString()}
                                {ex.reason && (
                                  <span className="text-gray-400 ml-1">({ex.reason})</span>
                                )}
                              </span>
                              <button
                                onClick={async () => {
                                  try {
                                    await api.bookings.removeRecurringException(
                                      selectedRecurring.id,
                                      ex.id
                                    )
                                    await loadRooms()
                                    const updated = (
                                      await api.bookings.listRecurring()
                                    ).recurringBookings.find((r) => r.id === selectedRecurring.id)
                                    if (updated) setSelectedRecurring(updated)
                                  } catch (err: any) {
                                    alert(err.message || 'Failed to restore date')
                                  }
                                }}
                                className="text-xs text-primary-600 hover:text-primary-700 font-medium"
                              >
                                Restore
                              </button>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>

                  <div className="flex justify-between gap-3 mt-6">
                    <div>
                      {selectedOccurrenceDate && (
                        <button
                          onClick={async () => {
                            if (
                              !confirm(
                                `Cancel the ${new Date(selectedOccurrenceDate + 'T00:00:00').toLocaleDateString()} occurrence?`
                              )
                            )
                              return
                            try {
                              await api.bookings.addRecurringException(selectedRecurring.id, {
                                date: selectedOccurrenceDate,
                              })
                              setSelectedRecurring(null)
                              await loadRooms()
                            } catch (err: any) {
                              alert(err.message || 'Failed to cancel date')
                            }
                          }}
                          className="px-4 py-2 text-amber-700 bg-amber-50 hover:bg-amber-100 rounded-lg transition-colors text-sm font-medium"
                        >
                          Skip This Date
                        </button>
                      )}
                    </div>
                    <div className="flex gap-3">
                      <button
                        onClick={() => setSelectedRecurring(null)}
                        className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
                      >
                        Close
                      </button>
                      <button
                        onClick={() => handleDeleteRecurring(selectedRecurring.id)}
                        className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
                      >
                        Delete Series
                      </button>
                    </div>
                  </div>
                </>
              )}
            </div>
          </div>
        )}

        {/* Complete Your Booking Modal */}
        {showPendingBookingModal &&
          pendingBookingData &&
          (() => {
            const room = rooms.find((r) => r.id === pendingBookingData.roomId)
            const building = buildings.find((b) => b.id === room?.buildingId)

            let suggestedSlots: string[] = []
            if (pendingBookingData.preferredSlots) {
              suggestedSlots = pendingBookingData.preferredSlots
                .split(',')
                .map((s) => s.trim())
                .filter(Boolean)
            } else if (pendingBookingData.preferredTime) {
              const timeMatch = pendingBookingData.preferredTime.match(
                /(\d{1,2}):?(\d{2})?\s*(AM|PM)?/gi
              )
              if (timeMatch) {
                suggestedSlots = timeMatch
                  .map((str) => {
                    const match = str.match(/(\d{1,2}):?(\d{2})?\s*(AM|PM)?/i)
                    if (!match || !match[1]) return null
                    let hours = parseInt(match[1])
                    const minutes = match[2] ? parseInt(match[2]) : 0
                    const period = match[3]?.toUpperCase()
                    if (period === 'PM' && hours !== 12) hours += 12
                    if (period === 'AM' && hours === 12) hours = 0
                    return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}`
                  })
                  .filter((s): s is string => s !== null)
              }
            }

            const suggestedStartTime = suggestedSlots[0] || '09:00'
            const suggestedEndTime =
              suggestedSlots.length > 0 ? suggestedSlots[suggestedSlots.length - 1] : '10:00'

            return (
              <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
                <div className="bg-white rounded-xl shadow-xl w-full max-w-lg mx-4">
                  <div className="bg-primary-50 px-6 py-4 rounded-t-xl border-b border-primary-100">
                    <h2 className="text-lg font-semibold text-primary-900">
                      Complete Your Booking
                    </h2>
                    <p className="text-sm text-primary-700 mt-1">
                      Your access request was approved! Let's finish booking your room.
                    </p>
                  </div>

                  <div className="p-6 space-y-4">
                    <div className="bg-gray-50 rounded-lg p-4 space-y-2">
                      <div className="flex items-center gap-2 text-sm">
                        <Building2 className="w-4 h-4 text-gray-400" />
                        <span className="text-gray-600">Room:</span>
                        <span className="font-medium text-gray-900">
                          {room?.name || 'Unknown Room'}
                        </span>
                      </div>
                      {building && (
                        <div className="flex items-center gap-2 text-sm">
                          <Home className="w-4 h-4 text-gray-400" />
                          <span className="text-gray-600">Building:</span>
                          <span className="text-gray-900">{building.name}</span>
                        </div>
                      )}
                      <div className="flex items-center gap-2 text-sm">
                        <Calendar className="w-4 h-4 text-gray-400" />
                        <span className="text-gray-600">Requested Date:</span>
                        <span className="text-gray-900">
                          {new Date(
                            pendingBookingData.preferredDate + 'T12:00:00'
                          ).toLocaleDateString('en-GB', {
                            weekday: 'long',
                            day: 'numeric',
                            month: 'long',
                            year: 'numeric',
                          })}
                        </span>
                      </div>
                      {pendingBookingData.preferredTime && (
                        <div className="flex items-center gap-2 text-sm">
                          <span className="text-gray-600 ml-6">Requested Time:</span>
                          <span className="text-gray-900">{pendingBookingData.preferredTime}</span>
                        </div>
                      )}
                      {pendingBookingData.message && (
                        <div className="mt-2 pt-2 border-t border-gray-200">
                          <p className="text-sm text-gray-600 italic">
                            "{pendingBookingData.message}"
                          </p>
                        </div>
                      )}
                    </div>

                    <p className="text-sm text-gray-600">
                      Click below to open the booking form with your requested details pre-filled.
                    </p>
                  </div>

                  <div className="px-6 py-4 bg-gray-50 rounded-b-xl flex gap-3 justify-end">
                    <button
                      onClick={() => {
                        setShowPendingBookingModal(false)
                        setPendingBookingData(null)
                      }}
                      className="px-4 py-2 text-gray-700 hover:bg-gray-200 rounded-lg transition-colors"
                    >
                      Maybe Later
                    </button>
                    <button
                      onClick={async () => {
                        setShowPendingBookingModal(false)
                        await openBookingModal(pendingBookingData.roomId, {
                          date: pendingBookingData.preferredDate,
                          startTime: suggestedStartTime,
                          endTime: suggestedEndTime,
                          slots: suggestedSlots,
                          notes: pendingBookingData.message || '',
                        })
                        setPendingBookingData(null)
                      }}
                      className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors"
                    >
                      Complete Booking
                    </button>
                  </div>
                </div>
              </div>
            )
          })()}
      </DashboardLayout>
    </>
  )
}

// Simple helper components (no duplicates of navigation)
function StatCard({
  title,
  shortTitle,
  value,
  description,
  shortDescription,
  onClick,
}: {
  title: string
  shortTitle?: string
  value: string
  description: string
  shortDescription?: string
  onClick?: () => void
}) {
  const className = `bg-white rounded-xl border border-gray-200 p-2 sm:p-6 ${onClick ? 'cursor-pointer hover:border-primary-300 hover:shadow-sm transition-all' : ''}`
  return (
    <div className={className} onClick={onClick}>
      <div className="text-xs sm:text-sm font-medium text-gray-500 mb-0.5 sm:mb-1">
        <span className="hidden sm:inline">{title}</span>
        <span className="sm:hidden">{shortTitle || title}</span>
      </div>
      <div className="text-xl sm:text-3xl font-bold text-gray-900 mb-0.5 sm:mb-1">{value}</div>
      <div className="text-xs sm:text-sm text-gray-500">
        <span className="hidden sm:inline">{description}</span>
        <span className="sm:hidden">{shortDescription || description}</span>
      </div>
    </div>
  )
}

function QuickAction({
  icon: Icon,
  title,
  description,
  href,
}: {
  icon: any
  title: string
  description: string
  href: string
}) {
  return (
    <Link
      to={href}
      className="flex items-start gap-3 sm:gap-4 p-3 sm:p-4 rounded-lg border border-gray-200 hover:border-primary-300 hover:bg-primary-50/50 transition-colors"
    >
      <div className="w-8 h-8 sm:w-10 sm:h-10 rounded-lg bg-primary-100 flex items-center justify-center text-primary-600 flex-shrink-0">
        <Icon className="w-4 h-4 sm:w-5 sm:h-5" />
      </div>
      <div className="min-w-0">
        <div className="font-medium text-gray-900 text-sm sm:text-base">{title}</div>
        <div className="text-xs sm:text-sm text-gray-500">{description}</div>
      </div>
    </Link>
  )
}
